import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';
import { ageAndYearLabel } from '../utils/childAge';
import { ChildProfileDrawer } from './ChildProfileDrawer';

/**
 * DailyRoster
 * ------------
 * Today's attendance roster — used on both the AdminDashboard and
 * StaffDashboard. For each child registered with the org we show:
 *   - present  — already checked in (with who delivered & at what time)
 *   - departed — already checked out (with who collected)
 *   - expected — not yet arrived
 *   - absent   — marked absent for the day
 *
 * Two primary actions:
 *   - ✅ Check in     — opens modal to capture deliverer's name + relationship
 *   - 👋 Check out   — opens modal to capture collector's name + relationship
 *
 * Why this matters: chain of custody is the core safeguarding requirement
 * for any childcare setting. Until now the platform had backend support for
 * custody events but no daily-attendance UI for the centre staff.
 */
export const DailyRoster = ({ allowEdit = true, onMessageParent = null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // { kind: 'in'|'out', child }
  const [form, setForm] = useState({ name: '', relationship: 'parent', method: 'parent', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [profileChildId, setProfileChildId] = useState(null); // opens ChildProfileDrawer
  const [hoveredRow, setHoveredRow] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await api.getTodayRoster();
      setData(d);
    } catch (e) {
      setError(e.message || 'Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openCheckIn = (child) => {
    setForm({ name: '', relationship: 'parent', method: 'parent', notes: '' });
    setModal({ kind: 'in', child });
  };
  const openCheckOut = (child) => {
    // Default the collector's name to the first authorised collector if any
    const def = (child.authorised_collectors || [])[0];
    setForm({
      name: def?.full_name || '',
      relationship: def?.relationship || 'parent',
      method: 'parent',
      notes: '',
    });
    setModal({ kind: 'out', child });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!modal) return;
    if (!form.name.trim()) {
      alert('Please enter the name of the person delivering or collecting the child.');
      return;
    }
    setSubmitting(true);
    try {
      if (modal.kind === 'in') {
        await api.attendanceCheckIn({
          child_id: modal.child.child_id,
          delivered_by_name: form.name.trim(),
          delivered_by_relationship: form.relationship,
          delivery_method: form.method,
          notes: form.notes || null,
        });
      } else {
        await api.attendanceCheckOut({
          child_id: modal.child.child_id,
          collected_by_name: form.name.trim(),
          collected_by_relationship: form.relationship,
          departure_method: form.method,
          notes: form.notes || null,
        });
      }
      setModal(null);
      await reload();
    } catch (err) {
      alert(err.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const markAbsent = async (child) => {
    if (!window.confirm(`Mark ${child.first_name} as absent today?`)) return;
    try {
      await api.attendanceMarkAbsent({ child_id: child.child_id });
      await reload();
    } catch (err) {
      alert(err.message || 'Failed');
    }
  };

  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
  const StatusPill = ({ status }) => {
    const map = {
      present:  { bg: 'hsl(140 50% 90%)', fg: 'hsl(140 60% 25%)', label: '✅ Present' },
      departed: { bg: 'hsl(220 60% 92%)', fg: 'hsl(220 60% 30%)', label: '👋 Departed' },
      expected: { bg: 'hsl(40 90% 92%)',  fg: 'hsl(40 70% 30%)',  label: '⏳ Expected' },
      absent:   { bg: 'hsl(0 0% 90%)',    fg: 'hsl(0 0% 30%)',    label: '🚫 Absent' },
    };
    const s = map[status] || map.expected;
    return (
      <span style={{
        display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: 4,
        background: s.bg, color: s.fg, fontSize: '0.75rem', fontWeight: 600,
      }}>{s.label}</span>
    );
  };

  if (loading) return <div className="card" data-testid="roster-loading">Loading today's roster…</div>;
  if (error) return <div className="card" style={{ color: 'hsl(0 70% 35%)' }} data-testid="roster-error">{error}</div>;
  if (!data || data.count === 0) {
    return (
      <div className="card empty-state" data-testid="roster-empty">
        <div style={{ fontSize: '2rem' }}>🪑</div>
        <h3>No children registered yet</h3>
        <p>Once children are added they'll appear here for daily check-in / check-out.</p>
      </div>
    );
  }

  // Group by status for clearer scanning
  const groups = { present: [], expected: [], departed: [], absent: [] };
  for (const c of data.children) {
    (groups[c.attendance.status] || groups.expected).push(c);
  }

  const summary = (
    <div style={{
      display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem',
    }} data-testid="roster-summary">
      <Stat label="Expected" count={data.count} tone="default" />
      <Stat label="Present" count={groups.present.length} tone="good" />
      <Stat label="Departed" count={groups.departed.length} tone="info" />
      <Stat label="Absent" count={groups.absent.length} tone="muted" />
    </div>
  );

  const renderRow = (c) => {
    const isHovered = hoveredRow === c.child_id;
    const openProfile = () => setProfileChildId(c.child_id);
    return (
    <div
      key={c.child_id}
      data-testid={`roster-row-${c.child_id}`}
      role="button"
      tabIndex={0}
      onClick={openProfile}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openProfile();
        }
      }}
      onMouseEnter={() => setHoveredRow(c.child_id)}
      onMouseLeave={() => setHoveredRow(null)}
      title={`View ${c.first_name}'s profile`}
      aria-label={`Open profile for ${c.first_name} ${c.last_name}`}
      style={{
        padding: '0.75rem', marginBottom: '0.5rem',
        border: '1px solid hsl(var(--border))', borderRadius: 8,
        display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', gap: '0.75rem',
        alignItems: 'center',
        cursor: 'pointer',
        background: isHovered ? 'hsl(var(--muted) / 0.4)' : 'transparent',
        boxShadow: isHovered ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
        transition: 'background-color 150ms ease, box-shadow 150ms ease',
        outline: 'none',
      }}
      onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 2px hsl(var(--primary) / 0.4)'; }}
      onBlur={(e) => { e.currentTarget.style.boxShadow = isHovered ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'; }}
    >
      <RosterAvatar photoUrl={c.photo_url} firstName={c.first_name} lastName={c.last_name} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
          <span style={{ textDecoration: isHovered ? 'underline hsl(var(--primary) / 0.5)' : 'none', textUnderlineOffset: 3 }}>
            {c.first_name} {c.last_name}
          </span>
          <StatusPill status={c.attendance.status} />
          <span
            aria-hidden="true"
            style={{
              fontSize: '0.85rem',
              color: 'hsl(var(--muted-foreground))',
              opacity: isHovered ? 1 : 0.55,
              transition: 'opacity 150ms ease',
            }}
          >
            ›
          </span>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
          {(() => {
            const lbl = ageAndYearLabel(c.date_of_birth);
            return lbl || (c.age_group ? `Age group ${c.age_group}` : '');
          })()}
          {c.school_info?.school_name && (
            <> &nbsp;·&nbsp; <span title={c.school_info.school_meeting_point ? `Pickup at ${c.school_info.school_meeting_point}` : 'School'}>
              🏫 {c.school_info.school_name}
              {c.school_info.school_class ? ` (${c.school_info.school_class})` : ''}
            </span></>
          )}
          {c.attendance.checked_in_at && (
            <> &nbsp;·&nbsp; In {fmtTime(c.attendance.checked_in_at)} by <strong>{c.attendance.checked_in_by_name}</strong></>
          )}
          {c.attendance.checked_out_at && (
            <> &nbsp;·&nbsp; Out {fmtTime(c.attendance.checked_out_at)} by <strong>{c.attendance.checked_out_by_name}</strong></>
          )}
        </div>
        {(c.allergies?.length || c.special_needs?.has_sen) && (
          <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
            {c.allergies?.length > 0 && (
              <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: 4, background: 'hsl(20 80% 92%)', color: 'hsl(20 70% 35%)' }}>
                ⚠️ Allergies: {c.allergies.slice(0, 2).join(', ')}{c.allergies.length > 2 ? '…' : ''}
              </span>
            )}
            {c.special_needs?.has_sen && (
              <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: 4, background: 'hsl(280 60% 92%)', color: 'hsl(280 50% 35%)' }}>SEND</span>
            )}
          </div>
        )}
      </div>
      {allowEdit && (
        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}
        >
          {c.attendance.status === 'expected' && (
            <>
              <button className="btn btn-primary" style={{ fontSize: '0.85rem' }}
                onClick={() => openCheckIn(c)} data-testid={`checkin-${c.child_id}`}>
                ✅ Check in
              </button>
              <button className="btn btn-ghost" style={{ fontSize: '0.85rem' }}
                onClick={() => markAbsent(c)} data-testid={`absent-${c.child_id}`}>
                🚫 Absent
              </button>
            </>
          )}
          {c.attendance.status === 'present' && (
            <button className="btn btn-primary" style={{ fontSize: '0.85rem' }}
              onClick={() => openCheckOut(c)} data-testid={`checkout-${c.child_id}`}>
              👋 Check out
            </button>
          )}
          {onMessageParent && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: '0.78rem' }}
              onClick={() => onMessageParent(c)}
              data-testid={`roster-message-${c.child_id}`}
              title="Message this child's parent"
            >
              ✉️ Message parent
            </button>
          )}
        </div>
      )}
    </div>
    );
  };

  return (
    <div data-testid="daily-roster">
      {summary}
      {groups.present.length > 0 && (
        <Section title="Currently in" testId="roster-section-present">
          {groups.present.map(renderRow)}
        </Section>
      )}
      {groups.expected.length > 0 && (
        <Section title="Expected today" testId="roster-section-expected">
          {groups.expected.map(renderRow)}
        </Section>
      )}
      {groups.departed.length > 0 && (
        <Section title="Departed" testId="roster-section-departed">
          {groups.departed.map(renderRow)}
        </Section>
      )}
      {groups.absent.length > 0 && (
        <Section title="Absent" testId="roster-section-absent">
          {groups.absent.map(renderRow)}
        </Section>
      )}

      {/* Check in / Check out modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => !submitting && setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}
            data-testid={modal.kind === 'in' ? 'checkin-modal' : 'checkout-modal'}
          >
            <div className="modal-header">
              <h2 style={{ margin: 0 }}>
                {modal.kind === 'in' ? '✅ Check in' : '👋 Check out'} — {modal.child.first_name} {modal.child.last_name}
              </h2>
            </div>
            <form onSubmit={submit} autoComplete="off">
              {modal.kind === 'out' && (modal.child.authorised_collectors || []).length > 0 && (
                <div className="form-group">
                  <label>Quick pick (authorised collectors on file)</label>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {(modal.child.authorised_collectors || []).map((p, i) => (
                      <button
                        type="button"
                        key={i}
                        className="btn btn-ghost"
                        style={{ fontSize: '0.85rem' }}
                        onClick={() => setForm({
                          ...form,
                          name: p.full_name || '',
                          relationship: p.relationship || 'parent',
                        })}
                        data-testid={`collector-quick-${i}`}
                      >
                        {p.full_name} ({p.relationship || 'unknown'})
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>{modal.kind === 'in' ? 'Delivered by (name)' : 'Collected by (name)'}</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="custody-person-name"
                />
              </div>
              <div className="form-group">
                <label>Relationship</label>
                <select
                  value={form.relationship}
                  onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                  data-testid="custody-relationship"
                >
                  <option value="parent">Parent</option>
                  <option value="guardian">Guardian</option>
                  <option value="authorised_collector">Authorised collector</option>
                  <option value="grandparent">Grandparent</option>
                  <option value="driver">Driver</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>{modal.kind === 'in' ? 'Delivery method' : 'Departure method'}</label>
                <select
                  value={form.method}
                  onChange={(e) => setForm({ ...form, method: e.target.value })}
                  data-testid="custody-method"
                >
                  <option value="parent">In person (parent / collector)</option>
                  <option value="driver">Driver / minibus</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="custody-notes"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" disabled={submitting}
                  onClick={() => setModal(null)} data-testid="custody-cancel">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="custody-submit">
                  {submitting ? 'Saving…' : modal.kind === 'in' ? 'Check in' : 'Check out'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Child profile drawer — safeguarding-first view opened from any row */}
      <ChildProfileDrawer
        open={!!profileChildId}
        childId={profileChildId}
        onClose={() => setProfileChildId(null)}
      />
    </div>
  );
};

const Section = ({ title, children, testId }) => (
  <div style={{ marginBottom: '1.25rem' }} data-testid={testId}>
    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'hsl(var(--muted-foreground))' }}>
      {title}
    </h4>
    {children}
  </div>
);

const Stat = ({ label, count, tone }) => {
  const tones = {
    default: { bg: 'hsl(var(--muted))', fg: 'hsl(var(--foreground))' },
    good:    { bg: 'hsl(140 50% 92%)', fg: 'hsl(140 60% 25%)' },
    info:    { bg: 'hsl(220 60% 92%)', fg: 'hsl(220 60% 30%)' },
    muted:   { bg: 'hsl(0 0% 92%)',    fg: 'hsl(0 0% 30%)' },
  };
  const t = tones[tone] || tones.default;
  return (
    <div style={{ flex: 1, minWidth: 110, padding: '0.5rem 0.75rem', borderRadius: 8, background: t.bg, color: t.fg }}>
      <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{count}</div>
      <div style={{ fontSize: '0.75rem' }}>{label}</div>
    </div>
  );
};

/**
 * RosterAvatar — small circular photo (or coloured initials) for each roster row.
 * Uses a deterministic hue so the same child always gets the same fallback tint.
 */
const RosterAvatar = ({ photoUrl, firstName, lastName }) => {
  const initials = `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase() || '👶';
  // Hash the name to a stable hue so each child has a consistent colour
  const seed = `${firstName || ''}${lastName || ''}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) % 360;
  const hue = hash;
  const [broken, setBroken] = React.useState(false);
  const showImage = photoUrl && !broken;
  return (
    <div
      aria-hidden="true"
      data-testid="roster-avatar"
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        overflow: 'hidden',
        flex: '0 0 auto',
        background: showImage ? 'hsl(var(--muted))' : `hsl(${hue} 55% 88%)`,
        color: `hsl(${hue} 55% 28%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '0.9rem',
        border: '1px solid hsl(var(--border))',
        letterSpacing: 0.5,
      }}
    >
      {showImage ? (
        <img
          src={photoUrl}
          alt=""
          onError={() => setBroken(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : initials}
    </div>
  );
};

export default DailyRoster;
